/**
 * OrderDetailContent
 *
 * Self-contained order detail view that can be rendered
 * inside a Page (OrderDetail) or a Modal (Mockups quick view).
 */

import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  Divider,
  Banner,
  SkeletonBodyText,
  SkeletonDisplayText,
  Modal,
  DropZone,
  Select,
  TextField,
  Spinner,
  List,
  Checkbox,
} from '@shopify/polaris';
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrderDetail, useOrderActions, useGmailAccounts } from '../hooks';
import {
  STAGE_LABELS,
  STAGE_TONES,
  FILE_TYPE_LABELS,
  PRODUCT_TYPE_LABELS,
  type WorkflowStage,
  type FileType,
  type OrderFile,
  type Approval,
} from '../types';
import { reorderOrder } from '../api/orders';
import CommunicationCenter from './CommunicationCenter';
import { StoreBadge } from '../lib/storeUtils';

// Helper to format date
function formatDate(timestamp: string | { _seconds: number; _nanoseconds: number } | null | undefined): string {
  if (!timestamp) return 'Unknown';

  let date: Date;

  if (typeof timestamp === 'object' && '_seconds' in timestamp) {
    date = new Date(timestamp._seconds * 1000);
  } else {
    date = new Date(timestamp);
  }

  if (isNaN(date.getTime())) return 'Unknown';

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Helper to format file size
function formatFileSize(bytes: number | undefined): string {
  if (bytes === undefined || bytes === 0 || isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Helper to check if a file is an image
function isImageFile(mimeType: string | undefined): boolean {
  return !!mimeType && mimeType.startsWith('image/');
}

// Helper to get approval status for a file
function getFileApprovalStatus(
  fileId: string,
  approvals: Approval[]
): { status: 'pending' | 'approved' | 'rejected' | 'expired'; rejectionReason?: string } | null {
  for (const approval of approvals) {
    if (approval.mockupApprovals) {
      const match = approval.mockupApprovals.find(ma => ma.fileId === fileId);
      if (match) {
        return { status: match.status, rejectionReason: match.rejectionReason };
      }
    }
    if (approval.proofFileIds?.includes(fileId)) {
      return { status: approval.status };
    }
    if (approval.proofFileId === fileId) {
      return { status: approval.status };
    }
  }
  return null;
}

// Helper to get the display URL for a file
function getFileDisplayUrl(file: OrderFile): string | null {
  return file.externalUrl || file.downloadUrl || null;
}

// Available stages for manual transition
const TRANSITION_STAGES: WorkflowStage[] = [
  'DIGITAL_MOCKUP',
  'AWAITING_APPROVAL',
  'APPROVED',
  'DIGITIZED_MOLD_SENT',
  'DIGITIZED_MOLD_RECEIVED',
  'IN_PRODUCTION',
  'IN_QA',
  'READY_TO_SHIP',
  'SHIPPED',
  'COMPLETE',
  'ARCHIVED',
];

// File type options for upload
const FILE_TYPE_OPTIONS: { label: string; value: FileType }[] = [
  { label: 'Customer Artwork', value: 'customer_artwork' },
  { label: 'Digitization Source', value: 'digitization_source' },
  { label: 'Digitization Result', value: 'digitization_result' },
  { label: 'Proof', value: 'proof' },
  { label: 'Production File', value: 'production_file' },
  { label: 'Other', value: 'other' },
];

function OrderDetailSkeleton() {
  const renderCard = (lines: number) => (
    <Layout.Section>
      <Card>
        <BlockStack gap="400">
          <SkeletonDisplayText size="small" />
          <SkeletonBodyText lines={lines} />
        </BlockStack>
      </Card>
    </Layout.Section>
  );

  return (
    <Layout>
      {renderCard(3)}
      {renderCard(5)}
    </Layout>
  );
}

interface OrderDetailContentProps {
  orderId: string;
}

export default function OrderDetailContent({ orderId }: OrderDetailContentProps) {
  const navigate = useNavigate();

  // Fetch order data
  const { data, loading, error, refetch } = useOrderDetail(orderId);
  const actions = useOrderActions(orderId, refetch);
  const { accounts: gmailAccounts } = useGmailAccounts();

  // Modal states
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [transitionModalOpen, setTransitionModalOpen] = useState(false);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);

  // Upload form state
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadFileType, setUploadFileType] = useState<FileType>('customer_artwork');

  // Transition form state
  const [targetStage, setTargetStage] = useState<WorkflowStage>('IN_PRODUCTION');
  const [transitionNotes, setTransitionNotes] = useState('');

  // Approval form state
  const [selectedProofFile, setSelectedProofFile] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');

  // Re-order state
  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const [reorderQuantities, setReorderQuantities] = useState<Record<string, number>>({});
  const [reorderSkipApproval, setReorderSkipApproval] = useState(true);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [reorderError, setReorderError] = useState<string | null>(null);

  // Image preview modal
  const [previewImage, setPreviewImage] = useState<{ url: string; filename: string } | null>(null);

  // Handle file drop
  const handleFileDrop = useCallback(
    (_dropFiles: File[], acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        setUploadFiles((prev) => [...prev, ...acceptedFiles]);
      }
    },
    []
  );

  // Handle file upload
const handleUpload = useCallback(async () => {
  if (uploadFiles.length === 0) return;
  for (const file of uploadFiles) {
    const success = await actions.uploadFile(file, uploadFileType);
    if (!success) return;
  }
  setUploadModalOpen(false);
  setUploadFiles([]);
}, [uploadFiles, uploadFileType, actions]);

  // Handle stage transition
  const handleTransition = useCallback(async () => {
    const success = await actions.transition(targetStage, transitionNotes || undefined);
    if (success) {
      setTransitionModalOpen(false);
      setTransitionNotes('');
    }
  }, [targetStage, transitionNotes, actions]);

  // Handle send approval
  const handleSendApproval = useCallback(async () => {
    if (!selectedProofFile) return;
    const success = await actions.sendApprovalEmail(selectedProofFile, approvalNotes || undefined);
    if (success) {
      setApprovalModalOpen(false);
      setSelectedProofFile('');
      setApprovalNotes('');
    }
  }, [selectedProofFile, approvalNotes, actions]);

  // Open re-order modal with quantities pre-filled from current order
  const openReorderModal = useCallback(() => {
    if (!data) return;
    const quantities: Record<string, number> = {};
    for (const item of data.order.lineItems) {
      if (item.shopifyVariantId) {
        quantities[item.shopifyVariantId] = item.quantity;
      }
    }
    setReorderQuantities(quantities);
    setReorderSkipApproval(true);
    setReorderError(null);
    setReorderModalOpen(true);
  }, [data]);

  // Handle re-order submission
  const handleReorder = useCallback(async () => {
    if (!data) return;
    const lineItems = Object.entries(reorderQuantities)
      .filter(([, qty]) => qty > 0)
      .map(([variantId, quantity]) => ({ variantId, quantity }));

    if (lineItems.length === 0) {
      setReorderError('At least one line item with quantity > 0 is required');
      return;
    }

    setReorderLoading(true);
    setReorderError(null);
    try {
      const result = await reorderOrder(orderId, {
        lineItems,
        skipApproval: reorderSkipApproval,
      });
      setReorderModalOpen(false);
      navigate(`/orders/${result.newOrderId}`);
    } catch (err) {
      setReorderError(err instanceof Error ? err.message : 'Failed to create re-order');
    } finally {
      setReorderLoading(false);
    }
  }, [data, reorderQuantities, reorderSkipApproval, orderId, navigate]);

  // Handle file download
  const handleDownload = useCallback(
    (file: OrderFile) => {
      actions.downloadFile(file.id, file.originalFilename);
    },
    [actions]
  );

  if (loading && !data) {
    return <OrderDetailSkeleton />;
  }

  if (error || !data) {
    return (
      <Banner
        title="Error loading order"
        tone="critical"
        action={{ content: 'Retry', onAction: refetch }}
      >
        <p>{error || 'Order not found'}</p>
      </Banner>
    );
  }

  const { order, files, approvals, inboundEmails = [], emailLogs = [], gmailMessages = [] } = data;
  const proofFiles = files.filter((f) => f.type === 'proof' || f.type === 'mockup');

  return (
    <>
      <style>{`
        .order-detail-grid {
          display: grid;
          grid-template-columns: 1fr 400px;
          gap: 16px;
        }
        @media (max-width: 900px) {
          .order-detail-grid {
            grid-template-columns: 1fr;
          }
          .order-detail-grid .comm-sidebar {
            position: static !important;
            max-height: none !important;
            overflow-y: visible !important;
          }
        }
      `}</style>

      {/* Full-width top section — banners + summary cards */}
      <Layout>
        {actions.error && (
          <Layout.Section variant="fullWidth">
            <Banner tone="critical" onDismiss={() => {}}>
              <p>{actions.error}</p>
            </Banner>
          </Layout.Section>
        )}

        {order.isDraft && (
          <Layout.Section variant="fullWidth">
            <Banner tone="warning" title="Draft Order">
              <p>
                This is a draft order — payment has not been collected yet. When the draft order is
                completed in Shopify, it will automatically convert to a paid order and the workflow
                will continue.
              </p>
            </Banner>
          </Layout.Section>
        )}

        {/* Customer Info */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">
                Customer
              </Text>
              <Text as="p" variant="bodyMd">
                {order.customerName}
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                {order.customerEmail}
              </Text>
              {order.customerPhone && (
                <Text as="p" variant="bodyMd" tone="subdued">
                  {order.customerPhone}
                </Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Stage Progress */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="200">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  Current Stage
                </Text>
                <InlineStack gap="200">
                  <StoreBadge shopDomain={order.shopDomain} />
                  <Badge tone={order.productType === 'patch' ? 'info' : order.productType === 'both' ? 'info' : 'success'}>
                    {PRODUCT_TYPE_LABELS[order.productType] || 'Embroidery'}
                  </Badge>
                </InlineStack>
              </InlineStack>
              <Badge size="large" tone={STAGE_TONES[order.currentStage]}>
                {STAGE_LABELS[order.currentStage]}
              </Badge>
              <Button onClick={() => setTransitionModalOpen(true)}>
                Change Stage
              </Button>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Quick Stats */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">
                Summary
              </Text>
              <InlineStack gap="400">
                <BlockStack gap="100">
                  <Text as="span" variant="bodySm" tone="subdued">
                    Files
                  </Text>
                  <Text as="span" variant="headingMd">
                    {files.length}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="span" variant="bodySm" tone="subdued">
                    Approvals
                  </Text>
                  <Text as="span" variant="headingMd">
                    {approvals.length}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="span" variant="bodySm" tone="subdued">
                    Items
                  </Text>
                  <Text as="span" variant="headingMd">
                    {order.lineItems.length}
                  </Text>
                </BlockStack>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Two-column grid: order details left, communication center right */}
      <div className="order-detail-grid" style={{ marginTop: 16 }}>
        {/* Left column — order details */}
        <div>
          <Layout>

            {/* Production Assignments Cards */}
            {order.productionAssignments && order.productionAssignments.length > 0 && (
              <Layout.Section>
                <Card>
                  <BlockStack gap="400">
                    <InlineStack align="space-between">
                      <Text as="h2" variant="headingMd">
                        Production Assignments
                      </Text>
                      <Badge tone="info">
                        {`${order.productionAssignments.filter((a) => a.status === 'completed').length}/${order.productionAssignments.length} completed`}
                      </Badge>
                    </InlineStack>
                    <Divider />

                    {order.productionAssignments.map((assignment, idx) => {
                      // Derive product title from hat items' source line items
                      const assignmentProductTitles = new Set<string>();
                      for (const hat of assignment.hatItems) {
                        if (hat.sourceLineItemId) {
                          const li = order.lineItems.find((l) => l.shopifyLineItemId === hat.sourceLineItemId);
                          if (li) assignmentProductTitles.add(li.productTitle);
                        }
                      }
                      const assignmentProductTitle = [...assignmentProductTitles].join(', ');

                      return (
                      <BlockStack key={assignment.id} gap="300">
                        <InlineStack align="space-between" blockAlign="center">
                          <InlineStack gap="200" blockAlign="center">
                            <Text as="h3" variant="headingSm">
                              {assignmentProductTitle
                                ? `${assignmentProductTitle}${assignment.label ? ` - ${assignment.label}` : ''}`
                                : (assignment.label || `Assignment ${idx + 1}`)}
                            </Text>
                            <Badge tone={assignment.status === 'completed' ? 'success' : 'attention'}>
                              {assignment.status === 'completed' ? 'Completed' : 'Pending'}
                            </Badge>
                          </InlineStack>
                          <Text as="span" variant="bodySm" tone="subdued">
                            {assignment.hatItems.reduce((sum, h) => sum + h.quantity, 0)} hat{assignment.hatItems.reduce((sum, h) => sum + h.quantity, 0) !== 1 ? 's' : ''}
                          </Text>
                        </InlineStack>

                        {/* Hat items */}
                        <InlineStack gap="200" wrap>
                          {assignment.hatItems.map((hat, hidx) => (
                            <Badge key={hidx}>
                              {`${hat.quantity}x ${hat.color || 'Unknown'}${hat.size ? ` / ${hat.size}` : ''}${hat.isManualOverride ? ' (manual)' : ''}`}
                            </Badge>
                          ))}
                        </InlineStack>

                        {/* Production data for completed assignments */}
                        {assignment.status === 'completed' && assignment.productionData && (
                          <BlockStack gap="200">
                            <InlineStack gap="200" wrap>
                              {assignment.productionData.threadColors.map((color, cidx) => (
                                <Badge key={cidx} tone="info">{color}</Badge>
                              ))}
                            </InlineStack>
                            <InlineStack gap="800">
                              <BlockStack gap="100">
                                <Text as="span" variant="bodySm" tone="subdued">Stitch Count</Text>
                                <Text as="span" variant="headingSm">{assignment.productionData.stitchCount.toLocaleString()}</Text>
                              </BlockStack>
                              <BlockStack gap="100">
                                <Text as="span" variant="bodySm" tone="subdued">Dimensions</Text>
                                <Text as="span" variant="headingSm">
                                  {`${assignment.productionData.measurements.width}" × ${assignment.productionData.measurements.height}"`}
                                </Text>
                              </BlockStack>
                              <BlockStack gap="100">
                                <Text as="span" variant="bodySm" tone="subdued">Completed</Text>
                                <Text as="span" variant="bodySm">{formatDate(assignment.productionData.completedAt)}</Text>
                              </BlockStack>
                            </InlineStack>
                            {assignment.productionData.defects && assignment.productionData.defects.length > 0 && (
                              <InlineStack gap="200" wrap>
                                {assignment.productionData.defects.map((defect, didx) => (
                                  <Badge key={didx} tone="critical">{defect}</Badge>
                                ))}
                              </InlineStack>
                            )}
                            {assignment.productionData.notes && (
                              <Text as="p" variant="bodySm" tone="subdued">Notes: {assignment.productionData.notes}</Text>
                            )}
                          </BlockStack>
                        )}

                        {idx < order.productionAssignments!.length - 1 && <Divider />}
                      </BlockStack>
                      );
                    })}
                  </BlockStack>
                </Card>
              </Layout.Section>
            )}

            {/* Legacy Production Data Card */}
            {order.productionData && (!order.productionAssignments || order.productionAssignments.length === 0) && (
              <Layout.Section>
                <Card>
                  <BlockStack gap="400">
                    <InlineStack align="space-between">
                      <Text as="h2" variant="headingMd">
                        Production Details
                      </Text>
                      <Badge tone="success">Completed</Badge>
                    </InlineStack>
                    <Divider />

                    <BlockStack gap="200">
                      <Text as="span" variant="bodySm" tone="subdued">
                        Thread Colors
                      </Text>
                      <InlineStack gap="200" wrap>
                        {order.productionData.threadColors.map((color, idx) => (
                          <Badge key={idx} tone="info">{color}</Badge>
                        ))}
                      </InlineStack>
                    </BlockStack>

                    <InlineStack gap="800">
                      <BlockStack gap="100">
                        <Text as="span" variant="bodySm" tone="subdued">
                          Stitch Count
                        </Text>
                        <Text as="span" variant="headingMd">
                          {order.productionData.stitchCount.toLocaleString()}
                        </Text>
                      </BlockStack>
                      <BlockStack gap="100">
                        <Text as="span" variant="bodySm" tone="subdued">
                          Dimensions
                        </Text>
                        <Text as="span" variant="headingMd">
                          {order.productionData.measurements.width}" × {order.productionData.measurements.height}"
                        </Text>
                      </BlockStack>
                      <BlockStack gap="100">
                        <Text as="span" variant="bodySm" tone="subdued">
                          Completed
                        </Text>
                        <Text as="span" variant="bodyMd">
                          {formatDate(order.productionData.completedAt)}
                        </Text>
                      </BlockStack>
                    </InlineStack>

                    {order.productionData.defects && order.productionData.defects.length > 0 && (
                      <BlockStack gap="200">
                        <Text as="span" variant="bodySm" tone="subdued">
                          Defects Reported
                        </Text>
                        <InlineStack gap="200" wrap>
                          {order.productionData.defects.map((defect, idx) => (
                            <Badge key={idx} tone="critical">{defect}</Badge>
                          ))}
                        </InlineStack>
                      </BlockStack>
                    )}

                    {order.productionData.notes && (
                      <BlockStack gap="200">
                        <Text as="span" variant="bodySm" tone="subdued">
                          Production Notes
                        </Text>
                        <Text as="p" variant="bodyMd">
                          {order.productionData.notes}
                        </Text>
                      </BlockStack>
                    )}
                  </BlockStack>
                </Card>
              </Layout.Section>
            )}

            {/* Line Items */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Line Items
                  </Text>
                  {order.lineItems.map((item, index) => (
                    <BlockStack key={index} gap="100">
                      <InlineStack align="space-between">
                        <Text as="span" variant="bodyMd" fontWeight="semibold">
                          {item.productTitle}
                          {item.variantTitle && ` - ${item.variantTitle}`}
                          {` (x${item.quantity})`}
                        </Text>
                        {item.sku && (
                          <Badge tone="info">{`SKU: ${item.sku}`}</Badge>
                        )}
                      </InlineStack>
                      {item.properties.length > 0 && (
                        <Text as="span" variant="bodySm" tone="subdued">
                          {item.properties.map((p) => `${p.name}: ${p.value}`).join(', ')}
                        </Text>
                      )}
                    </BlockStack>
                  ))}
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Files */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between">
                    <Text as="h2" variant="headingMd">
                      Files
                    </Text>
                    <InlineStack gap="200">
                      {order.currentStage === 'APPROVED' && (
                        <Button
                          variant="primary"
                          onClick={() => actions.transition('DIGITIZED_MOLD_SENT', 'Sent for digitization/mold')}
                        >
                          Send for Digitization
                        </Button>
                      )}
                      <Button onClick={() => setUploadModalOpen(true)}>Upload File</Button>
                    </InlineStack>
                  </InlineStack>
                  <Divider />
                  {files.length === 0 ? (
                    <Text as="p" tone="subdued">
                      No files uploaded yet
                    </Text>
                  ) : (
                    <BlockStack gap="400">
                      {files.map((file) => {
                        const displayUrl = getFileDisplayUrl(file);
                        const canDisplayImage = isImageFile(file.mimeType) && displayUrl;
                        const fileSize = formatFileSize(file.sizeBytes);

                        return (
                          <InlineStack key={file.id} align="space-between" blockAlign="center">
                            <InlineStack gap="400" blockAlign="center">
                              {/* Thumbnail container */}
                              <div
                                style={{
                                  width: 80,
                                  height: 80,
                                  borderRadius: 8,
                                  overflow: 'hidden',
                                  backgroundColor: '#f4f6f8',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  cursor: canDisplayImage ? 'pointer' : 'default',
                                }}
                                onClick={canDisplayImage ? () => setPreviewImage({ url: displayUrl!, filename: file.originalFilename }) : undefined}
                              >
                                {canDisplayImage ? (
                                  <img
                                    src={displayUrl}
                                    alt={file.originalFilename}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                    }}
                                  />
                                ) : (
                                  <Text as="span" variant="bodySm" tone="subdued">
                                    {file.mimeType === 'application/octet-stream'
                                      ? (file.originalFilename.split('.').pop()?.toUpperCase() || 'FILE')
                                      : (file.mimeType.split('/')[1]?.toUpperCase() || 'FILE')}
                                  </Text>
                                )}
                              </div>
                              {/* File info */}
                              <BlockStack gap="100">
                                <Text as="span" variant="bodyMd">
                                  {file.originalFilename}
                                </Text>
                                <InlineStack gap="200">
                                  <Badge tone="info">{FILE_TYPE_LABELS[file.type]}</Badge>
                                  {file.externalUrl && (
                                    <Badge>External</Badge>
                                  )}
                                  {(() => {
                                    const approvalStatus = getFileApprovalStatus(file.id, approvals);
                                    if (!approvalStatus) return null;
                                    const toneMap: Record<string, 'success' | 'critical' | 'attention' | 'warning'> = {
                                      approved: 'success', rejected: 'critical', pending: 'attention', expired: 'warning'
                                    };
                                    const labelMap: Record<string, string> = {
                                      approved: 'Approved', rejected: 'Rejected', pending: 'Pending Approval', expired: 'Expired'
                                    };
                                    return <Badge tone={toneMap[approvalStatus.status]}>{labelMap[approvalStatus.status]}</Badge>;
                                  })()}
                                  {fileSize && (
                                    <Text as="span" variant="bodySm" tone="subdued">
                                      {fileSize}
                                    </Text>
                                  )}
                                </InlineStack>
                                {file.embroideryMetadata && !file.embroideryMetadata.error && (
                                  <InlineStack gap="300">
                                    <Text as="span" variant="bodySm" tone="subdued">
                                      {file.embroideryMetadata.totalStitches.toLocaleString()} stitches
                                    </Text>
                                    <Text as="span" variant="bodySm" tone="subdued">
                                      {file.embroideryMetadata.widthIn}" × {file.embroideryMetadata.heightIn}"
                                    </Text>
                                    {file.embroideryMetadata.colorChanges > 0 && (
                                      <Text as="span" variant="bodySm" tone="subdued">
                                        {file.embroideryMetadata.colorChanges} color{file.embroideryMetadata.colorChanges !== 1 ? 's' : ''}
                                      </Text>
                                    )}
                                  </InlineStack>
                                )}
                              </BlockStack>
                            </InlineStack>
                            <InlineStack gap="200">
                              {file.externalUrl ? (
                                <Button
                                  variant="plain"
                                  url={file.externalUrl}
                                  external
                                >
                                  View
                                </Button>
                              ) : (
                                <Button variant="plain" onClick={() => handleDownload(file)}>
                                  Download
                                </Button>
                              )}
                              <Button
                                variant="plain"
                                tone="critical"
                                onClick={() => {
                                  if (confirm('Delete this file?')) {
                                    actions.removeFile(file.id);
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            </InlineStack>
                          </InlineStack>
                        );
                      })}
                    </BlockStack>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Actions */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Quick Actions
                  </Text>
                  <InlineStack gap="200">
                    {order.currentStage === 'AWAITING_APPROVAL' && (
                      <Button
                        variant="primary"
                        onClick={() => actions.approveManually()}
                      >
                        Manually Approve
                      </Button>
                    )}
                    <Button onClick={() => setTransitionModalOpen(true)}>
                      Change Stage
                    </Button>
                    <Button onClick={() => setUploadModalOpen(true)}>
                      Upload File
                    </Button>
                    <Button onClick={openReorderModal}>
                      Re-Order
                    </Button>
                    <Button
                      tone="critical"
                      onClick={async () => {
                        if (
                          confirm(
                            `Are you sure you want to remove order ${order.shopifyOrderName} from the pipeline? This will delete all tracked data, files, and approvals for this order. This cannot be undone.`
                          )
                        ) {
                          const success = await actions.removeOrder();
                          if (success) {
                            navigate('/orders');
                          }
                        }
                      }}
                    >
                      Remove from Pipeline
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Stage History */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Stage History
                  </Text>
                  {order.stageHistory.length === 0 ? (
                    <Text as="p" tone="subdued">
                      No stage transitions yet
                    </Text>
                  ) : (
                    [...order.stageHistory].reverse().map((transition) => (
                      <InlineStack key={transition.id} gap="400">
                        <Text as="span" variant="bodySm" tone="subdued" breakWord={false}>
                          {formatDate(transition.timestamp)}
                        </Text>
                        <BlockStack gap="100">
                          <Text as="span" variant="bodySm">
                            {transition.from
                              ? `${STAGE_LABELS[transition.from]} → ${STAGE_LABELS[transition.to]}`
                              : `Started at ${STAGE_LABELS[transition.to]}`}
                          </Text>
                          <Text as="span" variant="bodySm" tone="subdued">
                            by {transition.performedByName || transition.performedBy} ({transition.performedByType})
                          </Text>
                          {transition.notes && (
                            <Text as="span" variant="bodySm" tone="subdued">
                              Note: {transition.notes}
                            </Text>
                          )}
                        </BlockStack>
                      </InlineStack>
                    ))
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>
          </Layout>
        </div>

        {/* Right column — Communication Center (sticky on desktop, stacks on mobile) */}
        <div className="comm-sidebar" style={{ position: 'sticky', top: 16, alignSelf: 'start', maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}>
          <CommunicationCenter
            order={order}
            approvals={approvals}
            inboundEmails={inboundEmails}
            emailLogs={emailLogs}
            gmailMessages={gmailMessages}
            gmailAccounts={gmailAccounts}
            onEmailSent={refetch}
          />
        </div>
      </div>

      {/* Upload Modal */}
      <Modal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        title="Upload Files"
        primaryAction={{
          content: uploadFiles.length > 1 ? `Upload ${uploadFiles.length} Files` : 'Upload',
          onAction: handleUpload,
          disabled: uploadFiles.length === 0,
          loading: actions.loading,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setUploadModalOpen(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Select
              label="File Type"
              options={FILE_TYPE_OPTIONS}
              value={uploadFileType}
              onChange={(v) => setUploadFileType(v as FileType)}
            />
            <DropZone onDrop={handleFileDrop} allowMultiple>
              {uploadFiles.length > 0 ? (
                <BlockStack gap="200" inlineAlign="center">
                  {uploadFiles.map((file, idx) => (
                    <InlineStack key={idx} gap="200" blockAlign="center">
                      <Text as="span" variant="bodyMd">
                        {file.name}
                      </Text>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {formatFileSize(file.size)}
                      </Text>
                      <Button
                        variant="plain"
                        tone="critical"
                        size="slim"
                        onClick={() => setUploadFiles((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        Remove
                      </Button>
                    </InlineStack>
                  ))}
                  <Text as="p" variant="bodySm" tone="subdued">
                    Drop more files or click to add
                  </Text>
                </BlockStack>
              ) : (
                <DropZone.FileUpload />
              )}
            </DropZone>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Transition Modal */}
      <Modal
        open={transitionModalOpen}
        onClose={() => {
          setTransitionModalOpen(false);
          actions.clearError();
        }}
        title="Change Stage"
        primaryAction={{
          content: 'Change',
          onAction: handleTransition,
          loading: actions.loading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setTransitionModalOpen(false);
              actions.clearError();
            },
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {actions.transitionError && (
              <Banner
                title="Stage transition not allowed"
                tone="critical"
                onDismiss={() => actions.clearError()}
              >
                <BlockStack gap="200">
                  <Text as="p" variant="bodyMd">
                    {actions.transitionError.message}
                  </Text>

                  {actions.transitionError.missingPrerequisites &&
                    actions.transitionError.missingPrerequisites.length > 0 && (
                      <BlockStack gap="100">
                        <Text as="p" variant="bodySm" fontWeight="semibold">
                          Missing prerequisites:
                        </Text>
                        <List type="bullet">
                          {actions.transitionError.missingPrerequisites.map((req, idx) => (
                            <List.Item key={idx}>{req}</List.Item>
                          ))}
                        </List>
                      </BlockStack>
                    )}

                  {actions.transitionError.availableTransitions &&
                    actions.transitionError.availableTransitions.length > 0 && (
                      <BlockStack gap="100">
                        <Text as="p" variant="bodySm" fontWeight="semibold">
                          Available transitions from {actions.transitionError.currentStageLabel}:
                        </Text>
                        <List type="bullet">
                          {actions.transitionError.availableTransitions.map((t) => (
                            <List.Item key={t.stage}>{t.label}</List.Item>
                          ))}
                        </List>
                      </BlockStack>
                    )}

                  {actions.transitionError.availableTransitions &&
                    actions.transitionError.availableTransitions.length === 0 && (
                      <Text as="p" variant="bodySm" tone="subdued">
                        No transitions available from this stage.
                      </Text>
                    )}
                </BlockStack>
              </Banner>
            )}

            <Text as="p" tone="subdued">
              Current stage: {STAGE_LABELS[order.currentStage]}
            </Text>
            <Select
              label="New Stage"
              options={TRANSITION_STAGES.map((s) => ({
                label: STAGE_LABELS[s],
                value: s,
              }))}
              value={targetStage}
              onChange={(v) => {
                setTargetStage(v as WorkflowStage);
                actions.clearError();
              }}
            />
            <TextField
              label="Notes (optional)"
              value={transitionNotes}
              onChange={setTransitionNotes}
              multiline={2}
              autoComplete="off"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Approval Modal */}
      <Modal
        open={approvalModalOpen}
        onClose={() => setApprovalModalOpen(false)}
        title="Send Approval Request"
        primaryAction={{
          content: 'Send',
          onAction: handleSendApproval,
          disabled: !selectedProofFile,
          loading: actions.loading,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setApprovalModalOpen(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="p" tone="subdued">
              Select a proof file to send for customer approval
            </Text>
            <Select
              label="Proof File"
              options={[
                { label: 'Select a proof file...', value: '' },
                ...proofFiles.map((f) => ({
                  label: f.originalFilename,
                  value: f.id,
                })),
              ]}
              value={selectedProofFile}
              onChange={setSelectedProofFile}
            />
            <TextField
              label="Notes for Customer (optional)"
              value={approvalNotes}
              onChange={setApprovalNotes}
              multiline={3}
              autoComplete="off"
              placeholder="Add any notes or instructions for the customer..."
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Re-Order Modal */}
      <Modal
        open={reorderModalOpen}
        onClose={() => setReorderModalOpen(false)}
        title={`Re-Order: ${order.shopifyOrderName}`}
        primaryAction={{
          content: 'Create Re-Order',
          onAction: handleReorder,
          loading: reorderLoading,
          disabled: Object.values(reorderQuantities).every((q) => q <= 0),
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setReorderModalOpen(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {reorderError && (
              <Banner tone="critical" onDismiss={() => setReorderError(null)}>
                <p>{reorderError}</p>
              </Banner>
            )}

            <BlockStack gap="200">
              <Text as="p" variant="bodyMd">
                Customer: <Text as="span" fontWeight="semibold">{order.customerName}</Text> ({order.customerEmail})
              </Text>
            </BlockStack>

            <Divider />

            <Text as="h3" variant="headingSm">Line Items</Text>
            {order.lineItems.map((item) => (
              <InlineStack key={item.shopifyVariantId || item.shopifyLineItemId} align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text as="span" variant="bodyMd" fontWeight="semibold">
                    {item.productTitle}
                  </Text>
                  {item.variantTitle && (
                    <Text as="span" variant="bodySm" tone="subdued">
                      {item.variantTitle}
                    </Text>
                  )}
                </BlockStack>
                <div style={{ width: 80 }}>
                  <TextField
                    label=""
                    labelHidden
                    type="number"
                    min={0}
                    value={String(reorderQuantities[item.shopifyVariantId] ?? 0)}
                    onChange={(val) => {
                      setReorderQuantities((prev) => ({
                        ...prev,
                        [item.shopifyVariantId]: parseInt(val) || 0,
                      }));
                    }}
                    autoComplete="off"
                  />
                </div>
              </InlineStack>
            ))}

            <Divider />

            <Checkbox
              label="Skip approval (repeat order — design was previously approved)"
              checked={reorderSkipApproval}
              onChange={setReorderSkipApproval}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        open={previewImage !== null}
        onClose={() => setPreviewImage(null)}
        title={previewImage?.filename || ''}
        size="large"
      >
        <Modal.Section>
          {previewImage && (
            <div style={{ textAlign: 'center' }}>
              <img
                src={previewImage.url}
                alt={previewImage.filename}
                style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
              />
            </div>
          )}
        </Modal.Section>
      </Modal>

      {/* Loading overlay */}
      {actions.loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <Spinner size="large" />
        </div>
      )}
    </>
  );
}
